import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Modal,
  TextField,
  Typography,
  Snackbar,
  Divider,
} from "@mui/material";
import FileUploadIcon from "@mui/icons-material/UploadFile";
import useHttp from "../../Hooks/use-http";

const UpdateVerificationDocuments = ({
  open,
  onClose,
  emailID,
  documentID,
  isUploadNew = false,
  onSuccess,
}) => {
  const { sendRequest, isLoading } = useHttp();
  const [documents, setDocuments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    const fetchDocumentData = async () => {
      try {
        const userEmail = emailID || localStorage.getItem("user");

        const masterRes = await sendRequest({
          url: "/documentsmaster",
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: { emailID: userEmail },
        });

        const masterDocs = masterRes?.documentMaster || [];

        if (isUploadNew) {
          const driverDocs = masterDocs
            .filter((doc) => doc.category === "Driver")
            .map((doc) => ({
              name: doc.documentName || "",
              type: doc.type,
              documentID: doc.documentID,
              number: "",
              category: doc.category,
              expiryDate: "",
              file: null,
              url: "",
              error: "",
              preview: "",
              isUploaded: false,
              required: doc.required,
              hasExpiry: doc.hasExpiry,
              existingUrl: "", // Track existing URL separately
              existingNumber: "", // Track existing number separately
              existingExpiryDate: "", // Track existing expiry separately
            }));

          setDocuments(driverDocs);
        } else if (documentID) {
          const fetchedDoc = await sendRequest({
            url: "/documentByDocument",
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: {
              emailID: userEmail,
              data: {
                emailID: userEmail,
                documentID: documentID,
              },
            },
          });

          const matchedDoc = masterDocs.find(
            (doc) => doc.documentID === documentID
          );

          if (matchedDoc) {
            const docData = fetchedDoc?.data || {};

            setDocuments([
              {
                name: matchedDoc.documentName || "",
                type: matchedDoc.type,
                documentID: matchedDoc.documentID,
                number: docData.documentNumber || "",
                category: matchedDoc.category,
                expiryDate: docData.expiryDate || "",
                file: null,
                url: docData.url || "",
                error: "",
                preview: "",
                isUploaded: false,
                required: matchedDoc.required,
                hasExpiry: matchedDoc.hasExpiry,
                existingUrl: docData.documentURL || "", // Store existing URL
                existingNumber: docData.documentNumber || "", // Store existing number
                existingExpiryDate: docData.expiryDate || "", // Store existing expiry
              },
            ]);
          }
        }
      } catch (error) {
        console.error("Error fetching document data", error);
      }
    };

    if (open) {
      fetchDocumentData();
    }
  }, [open, isUploadNew, documentID, emailID, sendRequest]);

  const documentChangeHandler = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      const preview = file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : file.name;

      setDocuments((prev) => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          file,
          preview,
          error: "",
        };
        return updated;
      });
    }
  };

  const uploadFileToBackend = async (index) => {
    setIsUploading(true);
    const file = documents[index].file;
    const uniqueID = emailID || localStorage.getItem("user");
    const docID = documents[index].documentID;

    try {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      const formData = new FormData();
      formData.append("file", file);
      formData.append("fileName", file?.name ?? "random");
      formData.append("folderName", "driver");
      formData.append("uniqueID", uniqueID);
      formData.append("documentID", docID);
      formData.append("fileExtension", fileExtension);

      const response = await fetch(
        "https://littleinfinity.in:4001/documentfileupload",
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();

      if (response.ok) {
        setDocuments((prev) => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            url: data?.data,
            isUploaded: true,
          };
          return updated;
        });
        setSnackbarMessage("File uploaded successfully!");
      } else {
        setSnackbarMessage("Upload failed: " + data?.message);
      }
    } catch (err) {
      console.error(err);
      setSnackbarMessage("Error uploading file!");
    } finally {
      setIsUploading(false);
      setSnackbarOpen(true);
    }
  };

  const handleSubmit = async () => {
    const userEmail = emailID || localStorage.getItem("user");
    const payload = documents.map((doc) => {
      const docPayload = {
        emailID: userEmail,
        documentID: doc.documentID,
        category: doc.category,
        documentNumber: doc.number || doc.existingNumber,
        documentURL: doc.url || doc.existingUrl,
        type: doc.type,
        isVerified: 0,
      };

      if (doc.hasExpiry) {
        docPayload.expiryDate = doc.expiryDate || doc.existingExpiryDate;
      }

      return docPayload;
    });

    try {
      const response = await sendRequest({
        url: "/addupdatedocument",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: {
          emailID: userEmail,
          data: payload,
        },
      });

      if (response?.status == "201" || response?.status == "200") {
        setSnackbarMessage("Documents submitted successfully!");
        if (onSuccess) onSuccess();
        onClose();
      } else {
        setSnackbarMessage("Failed to submit documents");
      }
    } catch (error) {
      console.error(error);
      setSnackbarMessage("Something went wrong during submission");
    } finally {
      setSnackbarOpen(true);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          maxWidth: 900,
          maxHeight: "90vh",
          overflowY: "auto",
          margin: "5vh auto",
          backgroundColor: "#fff",
          padding: 4,
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" mb={3}>
          {isUploadNew
            ? "Upload New Driver Documents"
            : "Update Driver Document"}
        </Typography>

        <Grid container spacing={3}>
          {documents.map((doc, index) => (
            <Grid item xs={12} key={index}>
              <Box
                sx={{
                  padding: 2,
                  border: "1px solid #ddd",
                  borderRadius: 2,
                  backgroundColor: "#fafafa",
                  boxShadow: 2,
                  "&:hover": { boxShadow: 4 },
                }}
              >
                {/* Document Name */}
                <Typography sx={{ fontWeight: "bold", mb: 2 }}>
                  {doc.name}{" "}
                  {doc.required && <span style={{ color: "red" }}>*</span>}
                </Typography>

                {/* Existing Document Section (if available) */}
                {doc.existingUrl && (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Current Document:
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          label="Document Number"
                          value={doc.existingNumber}
                          variant="standard"
                          size="small"
                          InputProps={{
                            readOnly: true,
                          }}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        {doc.hasExpiry && doc.existingExpiryDate && (
                          <TextField
                            fullWidth
                            label="Expiry Date"
                            value={doc.existingExpiryDate}
                            variant="standard"
                            size="small"
                            InputProps={{
                              readOnly: true,
                            }}
                          />
                        )}
                      </Grid>
                      <Grid item xs={4}>
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          height="100%"
                        >
                          {doc.existingUrl.startsWith("http") ? (
                            <img
                              src={doc.existingUrl}
                              alt="Current Document"
                              style={{
                                width: 60,
                                height: 60,
                                borderRadius: 5,
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <Typography variant="body2">
                              {doc.existingUrl}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                    <Divider sx={{ my: 2 }} />
                  </>
                )}

                {/* Update Document Section (always shown) */}
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {doc.existingUrl
                    ? "Update Document:"
                    : "Upload New Document:"}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Document Number"
                      value={doc.number}
                      onChange={(e) => {
                        const updated = [...documents];
                        updated[index].number = e.target.value;
                        setDocuments(updated);
                      }}
                      variant="standard"
                      size="small"
                      placeholder={
                        doc.existingNumber ? "Leave blank to keep current" : ""
                      }
                    />
                  </Grid>

                  <Grid item xs={4}>
                    {doc.hasExpiry && (
                      <TextField
                        fullWidth
                        label="Expiry Date"
                        type="date"
                        value={doc.expiryDate}
                        onChange={(e) => {
                          const updated = [...documents];
                          updated[index].expiryDate = e.target.value;
                          setDocuments(updated);
                        }}
                        variant="standard"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  </Grid>

                  <Grid item xs={4}>
                    <Button
                      variant="contained"
                      component="label"
                      fullWidth
                      size="small"
                      startIcon={<FileUploadIcon />}
                      sx={{ backgroundColor: "#1976d2", color: "#fff" }}
                    >
                      {doc.preview ? "File Selected" : "Select File"}
                      <input
                        type="file"
                        hidden
                        onChange={(e) => documentChangeHandler(index, e)}
                      />
                    </Button>

                    {doc.preview && (
                      <Box
                        mt={1}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                      >
                        {doc.preview.startsWith("blob:") ? (
                          <img
                            src={doc.preview}
                            alt="Preview"
                            style={{
                              width: 60,
                              height: 60,
                              borderRadius: 5,
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <Typography variant="body2">{doc.preview}</Typography>
                        )}
                        <Button
                          onClick={() => uploadFileToBackend(index)}
                          variant="contained"
                          sx={{
                            marginTop: 1,
                            backgroundColor: "#44B150",
                            width: "100%",
                          }}
                          disabled={isUploading}
                        >
                          {isUploading ? (
                            <CircularProgress size={20} />
                          ) : (
                            "Upload File"
                          )}
                        </Button>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box mt={3} textAlign="right">
          {/* <Button onClick={onClose} variant="outlined" sx={{ marginRight: 2 }}>
            Close
          </Button> */}
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              backgroundColor: "red",
              color: "white",
              marginRight: 2,
              "&:hover": {
                backgroundColor: "#cc0000", // slightly darker red on hover
              },
            }}
          >
            Close
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isLoading}
            sx={{ backgroundColor: "#1976d2", color: "#fff" }}
          >
            {isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Submit"
            )}
          </Button>
        </Box>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
        />
      </Box>
    </Modal>
  );
};

export default UpdateVerificationDocuments;
